// Finance → Receive payment (plan chunk C9, decision 184): the payment form of a customer's statement, a click from the
// sidebar. Pick who paid, then the amount goes on their oldest invoices first, exactly as on the statement.
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Empty, PageHead, PracticeBanner, QueryView } from '../components/ui';
import { formatMoney } from '../format';
import { useDebounced, useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { PaymentForm } from './StatementPage';

export function ReceivePaymentPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [customerId, setCustomer] = useSearchParam('customer');
  const [q, setQ] = useSearchParam('q');
  const term = useDebounced(q.trim());
  const list = useQuery({ queryKey: qk.customers(term, ''), queryFn: ({ signal }) => api.customers(term, signal), enabled: !customerId, placeholderData: keepPreviousData });
  // while QuickBooks holds the books, what is typed here is practice (decision 187): the practice invoices are listed
  const statement = useQuery({ queryKey: qk.statement(customerId, true), queryFn: ({ signal }) => api.statement(customerId, signal, true), enabled: !!customerId });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['customers', 'statement', customerId] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };
  return (
    <div className="stack" data-testid="receive-payment">
      <PageHead title={t('receivePaymentTitle')} back="/" />
      <PracticeBanner />
      <p className="muted">{t('receivePaymentHint')}</p>
      {!customerId ? (
        <>
          <label className="field">
            <span>{t('pickCustomerWhoPaid')}</span>
            <input type="search" value={q} autoFocus autoComplete="off" placeholder={t('search')} aria-label={t('pickCustomerWhoPaid')} onChange={(e) => setQ(e.target.value)} />
          </label>
          <QueryView query={list}>
            {(cs) =>
              cs.length === 0 ? (
                <Empty>{t('noResults')}</Empty>
              ) : (
                <ul className="list">
                  {cs.slice(0, 30).map((c) => (
                    <li key={c.id}>
                      <button type="button" className="row row-button" onClick={() => setCustomer(c.id)}>
                        <span className="row-main">
                          <span className="row-title">{c.name}</span>
                        </span>
                        <span className="row-end-value">{c.balance === null ? '—' : formatMoney(c.balance, intlLocale)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )
            }
          </QueryView>
        </>
      ) : (
        <QueryView query={statement}>
          {(s) => (
            <>
              <div className="contact-actions">
                <strong>{s.customerName}</strong>
                <span className="muted">
                  {t('balance')}: {s.balance === null ? '—' : formatMoney(s.balance, intlLocale)}
                </span>
                <button type="button" className="btn btn-ghost" onClick={() => setCustomer('')}>
                  {t('pickCustomerWhoPaid')}…
                </button>
                <Link className="btn btn-ghost" to={`/customers/${s.customerId}/statement`}>
                  {t('openStatement')}
                </Link>
              </div>
              {s.balanceFrom === null ? <p className="muted">{t('ledgerNotStarted')}</p> : <PaymentForm key={s.customerId} statement={s} presetInvoice={null} onDone={refresh} />}
            </>
          )}
        </QueryView>
      )}
    </div>
  );
}
